import React, { useState, useEffect, useCallback } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { DATE_DISPLAY_FORMAT, displayDate, toApiDate } from "../../utils/dateUtils";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Menu,
  LinearProgress,
  Chip,
  Alert,
  Breadcrumbs,
  Link,
  Skeleton,
  TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  ArrowBack,
  Download,
  FilterList,
  People,
  School,
  Assignment,
  EventAvailable,
  NavigateNext,
  TableChart,
  MenuBook,
  Quiz,
} from "@mui/icons-material";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";
import StatsCard from "../../components/data-display/StatsCard";
import StatusChip from "../../components/data-display/StatusChip";
import {
  REPORT_GRANULARITY as GRANULARITY,
  REPORT_PERIOD_OPTIONS as PERIOD_OPTIONS,
  buildAttendanceDoughnutChart,
  buildComparisonBarChart,
} from "../../utils/advancedReportCharts";
import api from "../../services/api";
import { downloadAdvancedReportCsv, downloadSubjectReportCsv } from "../../utils/advancedReportExportCsv";
import { formatPercent, roundToDecimals } from "../../utils/formatPercent";
import { toast } from "react-toastify";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

const AdvancedStudentReports = () => {
  const navigate = useNavigate();
  const routeParams = useParams();
  const [searchParams] = useSearchParams();
  const routeDepartmentId = routeParams.departmentId;
  const routeClassId = routeParams.classId;
  const routeStudentId = routeParams.studentId;
  const { isInInstituteContext } = useSelector((state) => state.instituteContext);
  const { user } = useSelector((state) => state.auth);
  const userRole = user?.role?.slug || user?.role || "student";
  const canViewGlobalData = userRole === "superadmin" && !isInInstituteContext;

  const normalizedRole = String(userRole).toLowerCase().replace(/_/g, "");
  const canDownloadAttendanceDefaulter =
    normalizedRole === "hod" ||
    normalizedRole === "admin" ||
    normalizedRole === "institute" ||
    normalizedRole === "superadmin";

  // Drill-down state
  const [granularity, setGranularity] = useState(GRANULARITY.DEPARTMENT);
  const [period, setPeriod] = useState("monthly");
  const [instituteFilter, setInstituteFilter] = useState("");
  const [institutes, setInstitutes] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  /** Omit from request when "all" so backend aggregates every exam type. */
  const [examType, setExamType] = useState("all");
  const [examTypeOptions, setExamTypeOptions] = useState([{ value: "all", label: "All exam types" }]);
  const [examTypesLoading, setExamTypesLoading] = useState(true);

  // Breadcrumb trail
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Data
  const [data, setData] = useState(null);
  const [studentsInClass, setStudentsInClass] = useState([]);
  const [loading, setLoading] = useState(false);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [defaulterDownloading, setDefaulterDownloading] = useState(false);
  const [exportDownloading, setExportDownloading] = useState(false);
  const [csvExporting, setCsvExporting] = useState(false);
  const [classSubjects, setClassSubjects] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState("");
  const [subjectReportData, setSubjectReportData] = useState(null);
  const [subjectReportLoading, setSubjectReportLoading] = useState(false);
  const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] = useState(null);

  const buildReportQuery = useCallback(() => {
    const q = new URLSearchParams();
    if (period) q.set("period", period);
    if (fromDate && toDate) {
      q.set("from", fromDate);
      q.set("to", toDate);
    }
    if (examType && examType !== "all") q.set("examType", examType);
    if (instituteFilter && canViewGlobalData) q.set("instituteId", String(instituteFilter));
    return q;
  }, [period, fromDate, toDate, examType, instituteFilter, canViewGlobalData]);

  const reportQueryString = useCallback(() => {
    const s = buildReportQuery().toString();
    return s ? `?${s}` : "";
  }, [buildReportQuery]);

  /** Avoid re-fetch loops: only replace selection state when identity fields change. */
  const mergeDepartmentSelection = useCallback((prev, next) => {
    if (!next) return null;
    if (
      prev?.id === next.id &&
      prev?.name === next.name &&
      prev?.code === next.code
    ) {
      return prev;
    }
    return next;
  }, []);

  const mergeClassSelection = useCallback((prev, next) => {
    if (!next) return null;
    if (
      prev?.id === next.id &&
      prev?.name === next.name &&
      prev?.section === next.section &&
      prev?.departmentId === next.departmentId &&
      prev?.departmentName === next.departmentName
    ) {
      return prev;
    }
    return next;
  }, []);

  const mergeStudentSelection = useCallback((prev, next) => {
    if (!next) return null;
    if (
      prev?.id === next.id &&
      prev?.name === next.name &&
      prev?.rollNo === next.rollNo &&
      prev?.email === next.email
    ) {
      return prev;
    }
    return next;
  }, []);

  useEffect(() => {
    const p = searchParams.get("period");
    const f = searchParams.get("from");
    const t = searchParams.get("to");
    const ex = searchParams.get("examType");
    const inst = searchParams.get("instituteId");
    if (p) setPeriod(p);
    if (f) setFromDate(f);
    if (t) setToDate(t);
    if (ex) setExamType(ex);
    if (inst && canViewGlobalData) setInstituteFilter(inst);
  }, [searchParams, canViewGlobalData]);

  useEffect(() => {
    let mounted = true;
    const loadExamTypes = async () => {
      setExamTypesLoading(true);
      try {
        const params = new URLSearchParams();
        if (instituteFilter && canViewGlobalData) {
          params.set("instituteId", String(instituteFilter));
        }
        const qs = params.toString();
        const res = await api.get(
          qs ? `/academic-info/exam-types/all?${qs}` : "/academic-info/exam-types/all"
        );
        const list = res?.data?.data || [];
        if (!mounted) return;
        const dynamic = [
          { value: "all", label: "All exam types" },
          ...(Array.isArray(list) ? list : []).map((t) => ({
            value: t.key,
            label: t.label || t.key,
          })),
        ];
        setExamTypeOptions(dynamic);
      } catch {
        if (mounted) setExamTypeOptions([{ value: "all", label: "All exam types" }]);
      } finally {
        if (mounted) setExamTypesLoading(false);
      }
    };
    loadExamTypes();
    return () => { mounted = false; };
  }, [instituteFilter, canViewGlobalData]);

  // Load institutes for superadmin
  useEffect(() => {
    if (!canViewGlobalData) return;
    let mounted = true;
    const loadInstitutes = async () => {
      try {
        const instituteService = (await import("../../services/instituteService")).default;
        const res = await instituteService.getInstitutes({ page: 1, limit: 50 });
        if (mounted && res?.data?.data) setInstitutes(res.data.data);
      } catch (e) {
        console.warn("Failed to load institutes:", e?.message || e);
      }
    };
    loadInstitutes();
    return () => { mounted = false; };
  }, [canViewGlobalData]);

  // Main data fetch
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const service = (await import("../../services/advancedReportService")).default;
        const params = {
          period,
          ...(fromDate && toDate ? { from: fromDate, to: toDate } : {}),
          ...(examType && examType !== "all" ? { examType } : {}),
          ...(instituteFilter && canViewGlobalData ? { instituteId: instituteFilter } : {}),
        };

        if (granularity === GRANULARITY.DEPARTMENT) {
          const res = await service.getDepartmentReport(params);
          const payload = res?.data?.data ?? null;
          if (mounted) setData(payload);
        } else if (granularity === GRANULARITY.CLASS) {
          const deptId =
            selectedDepartment?.id != null && selectedDepartment.id !== ""
              ? Number(selectedDepartment.id)
              : routeDepartmentId
                ? Number(routeDepartmentId)
                : null;
          const classId =
            selectedClass?.id != null && selectedClass.id !== ""
              ? Number(selectedClass.id)
              : routeClassId
                ? Number(routeClassId)
                : null;

          if (routeDepartmentId && !selectedDepartment?.name) {
            const deptRes = await service.getDepartmentReport(params);
            const dept = deptRes?.data?.data?.departments?.find(
              (d) => Number(d.id) === Number(routeDepartmentId)
            );
            if (mounted && dept) {
              setSelectedDepartment((prev) => mergeDepartmentSelection(prev, dept));
            }
          }

          const res = await service.getClassReport({
            ...params,
            ...(Number.isFinite(deptId) && deptId > 0 ? { departmentId: deptId } : {}),
            ...(Number.isFinite(classId) && classId > 0 ? { classId } : {}),
          });
          const payload = res?.data?.data ?? null;
          if (mounted) {
            setData(payload);
            if (routeClassId && payload?.classes?.length) {
              const cls =
                payload.classes.find((c) => Number(c.id) === Number(routeClassId)) ||
                payload.classes[0];
              if (cls) {
                setSelectedClass((prev) => mergeClassSelection(prev, cls));
              }
            }
          }
        } else if (granularity === GRANULARITY.INDIVIDUAL) {
          const studentKey = selectedStudent?.id ?? routeStudentId;
          if (studentKey) {
            const res = await service.getStudentDetailReport(studentKey, params);
            const payload = res?.data?.data ?? null;
            if (mounted) {
              setData(payload);
              const st = payload?.student;
              if (st) {
                setSelectedStudent((prev) =>
                  mergeStudentSelection(prev, {
                    id: Number(studentKey),
                    name: st.name,
                    studentId: st.studentId,
                    rollNumber: st.rollNumber ?? st.rollNo,
                    rollNo: st.rollNumber ?? st.rollNo,
                    email: st.email,
                  })
                );
              }
            }
          }
        }
      } catch (e) {
        console.warn("Failed to load report:", e?.message || e);
        if (mounted) setData(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [
    granularity,
    period,
    fromDate,
    toDate,
    examType,
    instituteFilter,
    canViewGlobalData,
    selectedDepartment?.id,
    selectedClass?.id,
    selectedStudent?.id,
    routeStudentId,
    routeClassId,
    routeDepartmentId,
    mergeDepartmentSelection,
    mergeClassSelection,
    mergeStudentSelection,
  ]);

  useEffect(() => {
    if (!selectedSubjectId || !selectedClass?.id) {
      setSubjectReportData(null);
      return;
    }
    let mounted = true;
    const loadSubjectReport = async () => {
      setSubjectReportLoading(true);
      try {
        const service = (await import("../../services/advancedReportService")).default;
        const res = await service.getSubjectReport({
          period,
          ...(fromDate && toDate ? { from: fromDate, to: toDate } : {}),
          ...(instituteFilter && canViewGlobalData ? { instituteId: instituteFilter } : {}),
          classId: selectedClass.id,
          subjectId: selectedSubjectId,
        });
        if (mounted) setSubjectReportData(res?.data?.data ?? null);
      } catch (e) {
        console.warn("Failed to load subject report:", e?.message || e);
        if (mounted) setSubjectReportData(null);
      } finally {
        if (mounted) setSubjectReportLoading(false);
      }
    };
    loadSubjectReport();
    return () => {
      mounted = false;
    };
  }, [
    selectedSubjectId,
    selectedClass?.id,
    period,
    fromDate,
    toDate,
    instituteFilter,
    canViewGlobalData,
  ]);

  // Load students when class is selected (for class → individual transition)
  const loadStudentsInClass = useCallback(async (classId) => {
    setStudentsLoading(true);
    try {
      const analyticsService = (await import("../../services/analyticsService")).default;
      const res = await analyticsService.getStudents({ classId, page: 1, limit: 500 });
      const payload = res?.data?.data || res?.data || [];
      setStudentsInClass(Array.isArray(payload) ? payload : []);
    } catch (e) {
      console.warn("Failed to load students:", e?.message || e);
      setStudentsInClass([]);
    } finally {
      setStudentsLoading(false);
    }
  }, []);

  const loadSubjectsForClass = useCallback(async (classId) => {
    try {
      const classSubjectService = (await import("../../services/classSubjectService")).default;
      const res = await classSubjectService.getSubjectsByClass(classId);
      const payload = res?.data?.data || res?.data || null;
      const subjects = payload?.subjects || payload?.data?.subjects || [];
      setClassSubjects(Array.isArray(subjects) ? subjects : []);
    } catch (e) {
      console.warn("Failed to load class subjects:", e?.message || e);
      setClassSubjects([]);
    }
  }, []);

  useEffect(() => {
    if (routeStudentId) {
      const sid = Number(routeStudentId);
      if (Number.isFinite(sid) && sid > 0) {
        setGranularity(GRANULARITY.INDIVIDUAL);
        setSelectedStudent((prev) => (prev?.id === sid ? prev : { id: sid }));
      }
      const cid = searchParams.get("classId");
      const did = searchParams.get("departmentId");
      if (cid) {
        const classNum = Number(cid);
        if (Number.isFinite(classNum) && classNum > 0) {
          setSelectedClass((prev) => (prev?.id === classNum ? prev : { id: classNum }));
          loadStudentsInClass(classNum);
          loadSubjectsForClass(classNum);
        }
      }
      if (did) {
        const deptNum = Number(did);
        if (Number.isFinite(deptNum) && deptNum > 0) {
          setSelectedDepartment((prev) => (prev?.id === deptNum ? prev : { id: deptNum }));
        }
      }
      return;
    }
    if (routeClassId) {
      const cid = Number(routeClassId);
      if (Number.isFinite(cid) && cid > 0) {
        setGranularity(GRANULARITY.CLASS);
        setSelectedClass((prev) => (prev?.id === cid ? prev : { id: cid }));
        setSelectedStudent(null);
        loadStudentsInClass(cid);
        loadSubjectsForClass(cid);
      }
      const did = searchParams.get("departmentId");
      if (did) {
        const deptNum = Number(did);
        if (Number.isFinite(deptNum) && deptNum > 0) {
          setSelectedDepartment((prev) => (prev?.id === deptNum ? prev : { id: deptNum }));
        }
      }
      return;
    }
    if (routeDepartmentId) {
      const did = Number(routeDepartmentId);
      if (Number.isFinite(did) && did > 0) {
        setGranularity(GRANULARITY.CLASS);
        setSelectedDepartment((prev) => (prev?.id === did ? prev : { id: did }));
        setSelectedClass(null);
        setSelectedStudent(null);
        setStudentsInClass([]);
      }
      return;
    }
    setGranularity(GRANULARITY.DEPARTMENT);
    setSelectedDepartment(null);
    setSelectedClass(null);
    setSelectedStudent(null);
    setStudentsInClass([]);
  }, [
    routeStudentId,
    routeClassId,
    routeDepartmentId,
    searchParams.get("classId"),
    searchParams.get("departmentId"),
    loadStudentsInClass,
    loadSubjectsForClass,
  ]);

  // Drill-down handlers
  const handleDepartmentClick = (dept) => {
    setSelectedDepartment(dept);
    setSelectedClass(null);
    setSelectedStudent(null);
    setStudentsInClass([]);
    setGranularity(GRANULARITY.CLASS);
    navigate(`/analytics/reports/department/${dept.id}${reportQueryString()}`);
  };

  const handleClassClick = (cls) => {
    setSelectedClass(cls);
    setSelectedStudent(null);
    setSelectedSubjectId("");
    loadStudentsInClass(cls.id);
    loadSubjectsForClass(cls.id);
    const q = buildReportQuery();
    if (selectedDepartment?.id) q.set("departmentId", String(selectedDepartment.id));
    const qs = q.toString();
    navigate(`/analytics/reports/class/${cls.id}${qs ? `?${qs}` : ""}`);
  };

  const handleStudentClick = (student) => {
    const q = buildReportQuery();
    if (selectedClass?.id) q.set("classId", String(selectedClass.id));
    if (selectedDepartment?.id) q.set("departmentId", String(selectedDepartment.id));
    const qs = q.toString();
    navigate(`/analytics/reports/student/${student.id}${qs ? `?${qs}` : ""}`);
  };

  const handleBreadcrumbClick = (level) => {
    if (level === "root") {
      navigate(`/analytics/reports${reportQueryString()}`);
      return;
    }
    if (level === "department" && selectedDepartment?.id) {
      navigate(`/analytics/reports/department/${selectedDepartment.id}${reportQueryString()}`);
      return;
    }
    if (level === "class" && selectedClass?.id) {
      const q = buildReportQuery();
      if (selectedDepartment?.id) q.set("departmentId", String(selectedDepartment.id));
      const qs = q.toString();
      navigate(`/analytics/reports/class/${selectedClass.id}${qs ? `?${qs}` : ""}`);
    }
  };

  const buildReportParams = () => ({
    period,
    ...(fromDate && toDate ? { from: fromDate, to: toDate } : {}),
    ...(examType && examType !== "all" ? { examType } : {}),
    ...(instituteFilter && canViewGlobalData ? { instituteId: instituteFilter } : {}),
    ...(selectedDepartment?.id ? { departmentId: selectedDepartment.id } : {}),
    ...(selectedClass?.id ? { classId: selectedClass.id } : {}),
    ...(selectedSubjectId ? { subjectId: selectedSubjectId } : {}),
  });

  const selectedSubjectName =
    classSubjects.find((s) => String(s.id) === String(selectedSubjectId))?.name || "";

  const triggerBlobDownload = (blob, filename) => {
    if (!blob) return;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "report.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  };

  const handleDownloadXlsx = async (kind) => {
    setExportDownloading(true);
    try {
      const service = (await import("../../services/advancedReportService")).default;
      const baseParams = buildReportParams();
      if (kind === "department") {
        const res = await service.downloadDepartmentReportXlsx(baseParams);
        triggerBlobDownload(res?.data, `department_report_${period}.xlsx`);
      } else if (kind === "class") {
        const res = await service.downloadClassReportXlsx(baseParams);
        triggerBlobDownload(res?.data, `class_report_${period}.xlsx`);
      } else if (kind === "subject") {
        if (!selectedClass?.id || !selectedSubjectId) {
          toast.error("Select a subject first.");
          return;
        }
        const res = await service.downloadSubjectReportXlsx(baseParams);
        const slug = selectedSubjectName.replace(/[^\w.-]+/g, "_").slice(0, 40) || selectedSubjectId;
        triggerBlobDownload(res?.data, `subject_${slug}_${period}.xlsx`);
      } else if (kind === "student") {
        const studentKey = selectedStudent?.id ?? selectedStudent?.rollNo;
        if (!studentKey) {
          toast.error("Select a student first.");
          return;
        }
        const res = await service.downloadStudentDetailReportXlsx(studentKey, baseParams);
        const slug = String(selectedStudent?.name || selectedStudent?.rollNo || "student")
          .replace(/[^\w.-]+/g, "_")
          .slice(0, 40);
        triggerBlobDownload(res?.data, `student_${slug}_${period}.xlsx`);
      }
    } catch (e) {
      console.warn("XLSX download failed:", e?.message || e);
      toast.error(e?.response?.data?.message || "Download failed.");
    } finally {
      setExportDownloading(false);
    }
  };

  const handlePrimaryDownload = (e) => {
    if (showStudentList && selectedSubjectId) {
      handleDownloadXlsx("subject");
      return;
    }
    openDownloadMenu(e);
  };

  const openDownloadMenu = (e) => setDownloadMenuAnchorEl(e.currentTarget);
  const closeDownloadMenu = () => setDownloadMenuAnchorEl(null);
  const isDownloadMenuOpen = Boolean(downloadMenuAnchorEl);


  const showStudentList = granularity === GRANULARITY.CLASS && selectedClass && !selectedStudent;

  /** Back control: one step within drill-down (matches breadcrumbs), not browser history. */
  const handleBackNavigation = () => {
    if (routeStudentId || selectedStudent) {
      handleBreadcrumbClick("class");
      return;
    }
    if (routeClassId || selectedClass) {
      handleBreadcrumbClick("department");
      return;
    }
    if (routeDepartmentId || (granularity === GRANULARITY.CLASS && selectedDepartment)) {
      handleBreadcrumbClick("root");
      return;
    }
    navigate("/dashboard");
  };

  const handleExportReport = async () => {
    if (showStudentList && selectedClass?.id && selectedSubjectId) {
      setCsvExporting(true);
      try {
        const service = (await import("../../services/advancedReportService")).default;
        const res = await service.getSubjectReport(buildReportParams());
        const subjectReport = res?.data?.data;
        if (!downloadSubjectReportCsv({ subjectReport, period, subjectName: selectedSubjectName })) {
          toast.error("No attendance data for this subject in the selected period.");
        }
      } catch (e) {
        console.warn("Subject CSV export failed:", e?.message || e);
        toast.error(e?.response?.data?.message || "Export failed.");
      } finally {
        setCsvExporting(false);
      }
      return;
    }
    downloadAdvancedReportCsv({
      granularity,
      data,
      period,
      selectedClass,
      selectedStudent,
      studentsInClass,
    });
  };

  const handleDownloadAttendanceDefaulter = async () => {
    if (!selectedClass?.id) return;
    setDefaulterDownloading(true);
    try {
      const service = (await import("../../services/advancedReportService")).default;
      const params = {
        classId: selectedClass.id,
        period,
        ...(fromDate && toDate ? { from: fromDate, to: toDate } : {}),
        ...(instituteFilter && canViewGlobalData ? { instituteId: instituteFilter } : {}),
        ...(selectedSubjectId ? { subjectId: selectedSubjectId } : {}),
      };
      const res = await service.downloadAttendanceDefaulterMatrix(params);
      const blob = res?.data;
      if (!blob || !(blob instanceof Blob)) {
        toast.error("Could not download attendance report.");
        return;
      }
      if (blob.type && blob.type.includes("application/json")) {
        const text = await blob.text();
        try {
          const j = JSON.parse(text);
          toast.error(j?.message || "Download failed.");
        } catch {
          toast.error("Download failed.");
        }
        return;
      }
      const cd = res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"];
      let filename = `Monthly_Attendance_${selectedClass.name || "class"}.xlsx`;
      if (cd) {
        const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd);
        if (m?.[1]) filename = decodeURIComponent(m[1].trim());
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.warn("Attendance defaulter download failed:", e?.message || e);
      toast.error(e?.response?.data?.message || "Could not download attendance report.");
    } finally {
      setDefaulterDownloading(false);
    }
  };

  const exportDisabled = (() => {
    if (loading || csvExporting) return true;
    if (studentsLoading) return true;
    if (showStudentList) {
      if (selectedSubjectId) return !selectedClass?.id;
      return !studentsInClass?.length;
    }
    if (granularity === GRANULARITY.INDIVIDUAL) return !data?.student;
    return !data;
  })();

  // Progress bar renderer for DataGrid
  const renderProgressCell = (params) => {
    const pct = roundToDecimals(params.value || 0);
    return (
    <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1 }}>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          flexGrow: 1,
          height: 8,
          borderRadius: 4,
          bgcolor: "grey.200",
          "& .MuiLinearProgress-bar": {
            bgcolor:
              params.value >= 90
                ? "success.main"
                : params.value >= 80
                ? "warning.main"
                : "error.main",
          },
        }}
      />
      <Typography variant="body2" fontWeight={600}>
        {formatPercent(pct)}
      </Typography>
    </Box>
    );
  };

  // Column definitions per granularity
  const departmentColumns = [
    { field: "name", headerName: "Department", flex: 1, minWidth: 180 },
    { field: "code", headerName: "Code", width: 100 },
    { field: "totalStudents", headerName: "Students", width: 100 },
    {
      field: "attendancePct",
      headerName: "Attendance %",
      width: 180,
      valueGetter: (params) => roundToDecimals(params.row.attendance?.percentage || 0),
      renderCell: renderProgressCell,
    },
    {
      field: "assignmentPct",
      headerName: "Assignment Completion %",
      width: 200,
      valueGetter: (params) => roundToDecimals(params.row.assignments?.completionRate || 0),
      renderCell: renderProgressCell,
    },
    {
      field: "marksPct",
      headerName: "Exam marks %",
      width: 150,
      valueGetter: (params) => roundToDecimals(params.row.marks?.percent || 0),
      renderCell: renderProgressCell,
    },
  ];

  const classColumns = [
    { field: "name", headerName: "Class", flex: 1, minWidth: 150 },
    { field: "section", headerName: "Section", width: 100 },
    { field: "departmentName", headerName: "Department", width: 150 },
    { field: "classTeacher", headerName: "Class Teacher", width: 150 },
    { field: "totalStudents", headerName: "Students", width: 100 },
    {
      field: "attendancePct",
      headerName: "Attendance %",
      width: 180,
      valueGetter: (params) => roundToDecimals(params.row.attendance?.percentage || 0),
      renderCell: renderProgressCell,
    },
    {
      field: "assignmentPct",
      headerName: "Assignment Completion %",
      width: 200,
      valueGetter: (params) => roundToDecimals(params.row.assignments?.completionRate || 0),
      renderCell: renderProgressCell,
    },
    {
      field: "marksPct",
      headerName: "Exam marks %",
      width: 150,
      valueGetter: (params) => roundToDecimals(params.row.marks?.percent || 0),
      renderCell: renderProgressCell,
    },
  ];

  const studentListColumns = [
    { field: "name", headerName: "Student", flex: 1, minWidth: 200 },
    { field: "email", headerName: "Email", flex: 1, minWidth: 200 },
    { field: "studentId", headerName: "Student ID", width: 130 },
    { field: "rollNo", headerName: "Roll Number", width: 130 },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: (params) => <StatusChip status={params.value} />,
    },
  ];

  const attendanceDetailColumns = [
    {
      field: "lectureDate",
      headerName: "Date",
      width: 120,
      valueGetter: (params) =>
        params.row.lectureDate ||
        (params.row.date ? displayDate(params.row.date, "—") : "—"),
    },
    { field: "subject", headerName: "Subject", flex: 1, minWidth: 180 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) =>
        params.value ? <StatusChip status={params.value} /> : "—",
    },
    {
      field: "method",
      headerName: "Method",
      width: 100,
      valueFormatter: (params) => params.value || "—",
    },
    {
      field: "markedAt",
      headerName: "Marked at",
      width: 160,
      valueFormatter: (params) =>
        params.value ? displayDate(params.value, "—") : "—",
    },
  ];

  const assignmentDetailColumns = [
    { field: "name", headerName: "Assignment", flex: 1, minWidth: 200 },
    { field: "subject", headerName: "Subject", width: 150 },
    { field: "classDisplayName", headerName: "Class", width: 150, valueGetter: (params) => params.row.classDisplayName || (params.row.className && params.row.classSection ? `${params.row.className} - ${params.row.classSection}` : params.row.className) || "—" },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => <StatusChip status={params.value} />,
    },
    {
      field: "score",
      headerName: "Score",
      width: 120,
      valueGetter: (params) =>
        params.row.score != null
          ? `${params.row.score}/${params.row.maxScore || 100}`
          : "—",
      sortComparator: (v1, v2, p1, p2) =>
        Number(p1.api.getRow(p1.id)?.score || 0) - Number(p2.api.getRow(p2.id)?.score || 0),
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      width: 130,
      valueFormatter: (params) =>
        params.value ? displayDate(params.value, "N/A") : "N/A",
    },
    {
      field: "submittedAt",
      headerName: "Submitted",
      width: 130,
      valueFormatter: (params) =>
        params.value ? displayDate(params.value, "N/A") : "N/A",
    },
    {
      field: "isLate",
      headerName: "Late",
      width: 80,
      renderCell: (params) =>
        params.value ? (
          <Chip label="Late" size="small" color="warning" />
        ) : null,
    },
  ];

  const materialDetailColumns = [
    { field: "name", headerName: "Material", flex: 1, minWidth: 200 },
    { field: "subject", headerName: "Subject", width: 150 },
    {
      field: "filesCount",
      headerName: "Files",
      width: 90,
      type: "number",
    },
    {
      field: "createdAt",
      headerName: "Uploaded",
      width: 130,
      valueFormatter: (params) =>
        params.value ? displayDate(params.value, "N/A") : "N/A",
    },
  ];

  const formatExamTypeLabel = (type) => {
    const t = String(type || "").trim();
    if (!t) return "—";
    if (t === "unitTest") return "Unit Test";
    if (t === "midTerm") return "Mid Term";
    return t.charAt(0).toUpperCase() + t.slice(1);
  };

  const examDetailColumns = [
    { field: "name", headerName: "Exam", flex: 1, minWidth: 180 },
    {
      field: "examType",
      headerName: "Type",
      width: 110,
      valueFormatter: (params) => formatExamTypeLabel(params.value),
    },
    { field: "subject", headerName: "Subject", width: 140 },
    {
      field: "examDate",
      headerName: "Date",
      width: 120,
      valueFormatter: (params) =>
        params.value ? displayDate(params.value, "N/A") : "N/A",
    },
    {
      field: "scoreDisplay",
      headerName: "Score",
      width: 110,
      valueGetter: (params) =>
        params.row.marksObtained != null
          ? `${params.row.marksObtained}/${params.row.totalMarks || "—"}`
          : "—",
    },
    {
      field: "percent",
      headerName: "%",
      width: 70,
      valueFormatter: (params) =>
        params.value != null ? formatPercent(params.value) : "—",
    },
    {
      field: "passed",
      headerName: "Result",
      width: 100,
      renderCell: (params) => {
        if (params.value == null) {
          return params.row.marked ? null : (
            <Chip label="Not marked" size="small" variant="outlined" />
          );
        }
        return (
          <Chip
            label={params.value ? "Pass" : "Fail"}
            size="small"
            color={params.value ? "success" : "error"}
          />
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      renderCell: (params) =>
        params.value ? <StatusChip status={params.value} /> : null,
    },
  ];

  // Summary stats
  const getSummaryStats = () => {
    if (selectedSubjectId && subjectReportData) {
      const students = Array.isArray(subjectReportData.students) ? subjectReportData.students : [];
      const lectures = Array.isArray(subjectReportData.lectures) ? subjectReportData.lectures : [];
      const avgAttendance =
        students.length > 0
          ? students.reduce((sum, s) => sum + Number(s.percentage || 0), 0) / students.length
          : 0;
      const totalPresent = students.reduce((sum, s) => sum + Number(s.present || 0), 0);
      const totalLectures = students.reduce((sum, s) => sum + Number(s.totalLectures || 0), 0);
      return [
        {
          value: students.length,
          label: `Students (${selectedSubjectName || "Subject"})`,
          color: "primary",
          icon: <School />,
        },
        {
          value: formatPercent(avgAttendance),
          label: "Avg Attendance (Subject)",
          color: "success",
          icon: <EventAvailable />,
        },
        {
          value: lectures.length || totalLectures,
          label: "Lectures Conducted",
          color: "info",
          icon: <Assignment />,
        },
        {
          value: totalPresent,
          label: "Total Present Marks",
          color: "warning",
          icon: <Quiz />,
        },
      ];
    }

    if (!data) return [];

    if (granularity === GRANULARITY.INDIVIDUAL) {
      return [
        { value: formatPercent(data.attendance?.percentage || 0), label: "Attendance", color: "success", icon: <EventAvailable /> },
        { value: formatPercent(data.assignments?.completionRate || 0), label: "Assignment Completion", color: "info", icon: <Assignment /> },
        { value: formatPercent(data.exams?.avgPercent ?? 0), label: "Exam Avg Score", color: "warning", icon: <Quiz /> },
        { value: data.materials?.total ?? 0, label: "Study Materials", color: "primary", icon: <MenuBook /> },
      ];
    }

    const summary = data.summary || {};
    const entityLabel = granularity === GRANULARITY.DEPARTMENT ? "Departments" : "Classes";
    const entityCount = granularity === GRANULARITY.DEPARTMENT ? summary.totalDepartments : summary.totalClasses;

    return [
      { value: entityCount || 0, label: `Total ${entityLabel}`, color: "primary", icon: <School /> },
      {
        value: formatPercent(summary.avgAttendanceExact ?? summary.avgAttendance ?? 0),
        label: "Avg Attendance",
        color: "success",
        icon: <EventAvailable />,
      },
      { value: formatPercent(summary.avgAssignmentCompletion || 0), label: "Avg Assignment Completion", color: "info", icon: <Assignment /> },
      { value: formatPercent(summary.avgMarksPercent ?? 0), label: "Avg exam marks", color: "warning", icon: <School /> },
    ];
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
        <IconButton onClick={handleBackNavigation} aria-label="Back">
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            Student Reports
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Drill-down analytics: Department → Class → Individual
          </Typography>
        </Box>

        {/* Right-side compact actions (aside) */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-end", maxWidth: 520 }}>
          {granularity === GRANULARITY.CLASS && selectedClass && !selectedStudent && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Subject</InputLabel>
              <Select value={selectedSubjectId} label="Subject" onChange={(e) => setSelectedSubjectId(e.target.value)}>
                <MenuItem value="">Select subject</MenuItem>
                {(classSubjects || []).map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {showStudentList && canDownloadAttendanceDefaulter && (
            <Button
              variant="outlined"
              startIcon={<TableChart />}
              onClick={handleDownloadAttendanceDefaulter}
              disabled={defaulterDownloading || studentsLoading || !selectedClass?.id}
            >
              {defaulterDownloading
                ? "Downloading…"
                : selectedSubjectId
                  ? "Download subject attendance"
                  : "Download attendance"}
            </Button>
          )}

          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handlePrimaryDownload}
            disabled={exportDownloading || loading || (showStudentList && selectedSubjectId && !selectedClass?.id)}
          >
            {exportDownloading
              ? "Downloading…"
              : showStudentList && selectedSubjectId
                ? "Download subject report"
                : "Download"}
          </Button>
          <Menu anchorEl={downloadMenuAnchorEl} open={isDownloadMenuOpen} onClose={closeDownloadMenu}>
            {granularity === GRANULARITY.DEPARTMENT && (
              <MenuItem
                onClick={() => {
                  closeDownloadMenu();
                  handleDownloadXlsx("department");
                }}
              >
                Department XLSX
              </MenuItem>
            )}
            {granularity === GRANULARITY.CLASS && !selectedStudent && !selectedSubjectId && (
              <MenuItem
                onClick={() => {
                  closeDownloadMenu();
                  handleDownloadXlsx("class");
                }}
              >
                Class XLSX
              </MenuItem>
            )}
            {granularity === GRANULARITY.CLASS && selectedClass && !selectedStudent && (
              <MenuItem
                disabled={!selectedSubjectId}
                onClick={() => {
                  closeDownloadMenu();
                  handleDownloadXlsx("subject");
                }}
              >
                Subject XLSX
              </MenuItem>
            )}
            {granularity === GRANULARITY.INDIVIDUAL && selectedStudent && (
              <>
                <MenuItem
                  onClick={() => {
                    closeDownloadMenu();
                    handleDownloadXlsx("student");
                  }}
                >
                  Student XLSX
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    closeDownloadMenu();
                    handleExportReport();
                  }}
                >
                  Student CSV
                </MenuItem>
              </>
            )}
          </Menu>

          <Button variant="outlined" startIcon={<Download />} onClick={handleExportReport} disabled={exportDisabled}>
            {csvExporting
              ? "Exporting…"
              : showStudentList && selectedSubjectId
                ? "Export subject CSV"
                : "Export CSV"}
          </Button>
        </Box>
      </Box>

      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          underline="hover"
          color={granularity === GRANULARITY.DEPARTMENT ? "text.primary" : "inherit"}
          onClick={() => handleBreadcrumbClick("root")}
          sx={{ fontWeight: granularity === GRANULARITY.DEPARTMENT ? 600 : 400 }}
        >
          All Departments
        </Link>
        {selectedDepartment && (
          <Link
            component="button"
            variant="body2"
            underline="hover"
            color={granularity === GRANULARITY.CLASS && !selectedStudent ? "text.primary" : "inherit"}
            onClick={() => handleBreadcrumbClick("department")}
            sx={{ fontWeight: granularity === GRANULARITY.CLASS && !selectedStudent ? 600 : 400 }}
          >
            {selectedDepartment.name}
          </Link>
        )}
        {selectedClass && (
          <Link
            component="button"
            variant="body2"
            underline="hover"
            color={showStudentList ? "text.primary" : "inherit"}
            onClick={() => handleBreadcrumbClick("class")}
            sx={{ fontWeight: showStudentList ? 600 : 400 }}
          >
            {selectedClass.name} {selectedClass.section ? `(${selectedClass.section})` : ""}
          </Link>
        )}
        {selectedStudent && (
          <Typography variant="body2" color="text.primary" fontWeight={600}>
            {selectedStudent.name}
          </Typography>
        )}
      </Breadcrumbs>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <FilterList color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              Filters
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {canViewGlobalData && (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Institute</InputLabel>
                  <Select
                    value={instituteFilter}
                    label="Institute"
                    onChange={(e) => setInstituteFilter(e.target.value)}
                  >
                    <MenuItem value="">All Institutes</MenuItem>
                    {institutes.map((inst) => (
                      <MenuItem key={inst.id} value={String(inst.id)}>
                        {inst.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Period</InputLabel>
                <Select
                  value={period}
                  label="Period"
                  onChange={(e) => {
                    setPeriod(e.target.value);
                    setFromDate("");
                    setToDate("");
                  }}
                >
                  {PERIOD_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Exam type</InputLabel>
                <Select
                  value={examType}
                  label="Exam type"
                  onChange={(e) => setExamType(e.target.value)}
                >
                  {examTypesLoading ? (
                    <MenuItem disabled>Loading exam types…</MenuItem>
                  ) : (
                  examTypeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="From"
                format={DATE_DISPLAY_FORMAT}
                value={fromDate ? new Date(fromDate) : null}
                onChange={(val) => setFromDate(toApiDate(val))}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="To"
                format={DATE_DISPLAY_FORMAT}
                value={toDate ? new Date(toDate) : null}
                onChange={(val) => setToDate(toApiDate(val))}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                size="medium"
                variant="outlined"
                onClick={() => {
                  setFromDate("");
                  setToDate("");
                }}
                disabled={!fromDate && !toDate}
                sx={{ height: 40 }}
              >
                Clear Date Range
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {(loading || subjectReportLoading) && <LinearProgress sx={{ mb: 2 }} />}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {loading || (selectedSubjectId && subjectReportLoading) ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Grid item xs={6} sm={3} key={`sk-stat-${i}`}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Skeleton variant="circular" width={40} height={40} sx={{ mb: 1.5 }} />
                  <Skeleton variant="text" width="60%" height={40} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width="80%" />
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          getSummaryStats().map((stat, i) => (
            <Grid item xs={6} sm={3} key={i}>
              <StatsCard
                value={stat.value}
                label={stat.label}
                icon={stat.icon}
                color={stat.color}
              />
            </Grid>
          ))
        )}
      </Grid>

      {!loading && (data?.labels?.departmentAttendanceTotal || data?.labels?.classAttendanceTotal) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {data.labels.departmentAttendanceTotal || data.labels.classAttendanceTotal}
        </Alert>
      )}

      {/* Charts (not shown for student list, subject-wise view, or individual detail) */}
      {!selectedSubjectId &&
        (granularity === GRANULARITY.DEPARTMENT || (granularity === GRANULARITY.CLASS && !selectedClass)) &&
        (loading || data) && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {loading ? (
            <>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Skeleton variant="text" width="50%" height={32} sx={{ mb: 2 }} />
                    <Skeleton variant="rounded" height={250} />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={8}>
                <Card>
                  <CardContent>
                    <Skeleton variant="text" width="45%" height={32} sx={{ mb: 2 }} />
                    <Skeleton variant="rounded" height={250} />
                  </CardContent>
                </Card>
              </Grid>
            </>
          ) : (
            data && (
              <>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        Attendance Breakdown
                      </Typography>
                      <Box sx={{ height: 250, display: "flex", justifyContent: "center" }}>
                        <Doughnut
                          data={buildAttendanceDoughnutChart({ granularity, data, selectedStudent })}
                          options={{
                            maintainAspectRatio: false,
                            plugins: { legend: { position: "bottom" } },
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        {granularity === GRANULARITY.DEPARTMENT ? "Department" : "Class"} Comparison
                      </Typography>
                      <Box sx={{ height: 250 }}>
                        <Bar
                          data={buildComparisonBarChart({ granularity, data })}
                          options={{
                            maintainAspectRatio: false,
                            plugins: { legend: { position: "top" } },
                            scales: {
                              y: { beginAtZero: true, max: 100, ticks: { callback: (v) => `${v}%` } },
                            },
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </>
            )
          )}
        </Grid>
      )}

      {/* Data Tables */}
      {granularity === GRANULARITY.DEPARTMENT && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Department-wise Report
            </Typography>
            <Box sx={{ height: 400 }}>
              <DataGrid
                rows={data?.departments || []}
                columns={departmentColumns}
                pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
                disableRowSelectionOnClick
                onRowClick={(params) => handleDepartmentClick(params.row)}
                loading={loading}
                sx={{ cursor: "pointer", border: "none" }}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {granularity === GRANULARITY.CLASS && !selectedClass && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Class-wise Report
              {selectedDepartment ? ` — ${selectedDepartment.name}` : ""}
            </Typography>
            <Box sx={{ height: 400 }}>
              <DataGrid
                rows={data?.classes || []}
                columns={classColumns}
                pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
                disableRowSelectionOnClick
                onRowClick={(params) => handleClassClick(params.row)}
                loading={loading}
                sx={{ cursor: "pointer", border: "none" }}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {showStudentList && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Students — {selectedClass.name} {selectedClass.section ? `(${selectedClass.section})` : ""}
            </Typography>
            {studentsLoading && <LinearProgress sx={{ mb: 1 }} />}
            <Box sx={{ height: 400 }}>
              <DataGrid
                rows={studentsInClass}
                columns={studentListColumns}
                pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
                disableRowSelectionOnClick
                onRowClick={(params) => handleStudentClick(params.row)}
                loading={studentsLoading}
                sx={{ cursor: "pointer", border: "none" }}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {granularity === GRANULARITY.INDIVIDUAL && selectedStudent && (
        <>
          {loading ? (
            <>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Skeleton variant="text" width="55%" height={28} sx={{ mb: 2 }} />
                      <Skeleton variant="rounded" height={250} />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Card>
                    <CardContent>
                      <Skeleton variant="text" width="40%" height={28} sx={{ mb: 1 }} />
                      <Skeleton variant="text" width="70%" sx={{ mb: 0.5 }} />
                      <Skeleton variant="text" width="85%" />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Skeleton variant="text" width="30%" height={32} sx={{ mb: 2 }} />
                  <Skeleton variant="rounded" height={400} />
                </CardContent>
              </Card>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Skeleton variant="text" width="35%" height={32} sx={{ mb: 2 }} />
                  <Skeleton variant="rounded" height={400} />
                </CardContent>
              </Card>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Skeleton variant="text" width="30%" height={32} sx={{ mb: 2 }} />
                  <Skeleton variant="rounded" height={400} />
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width="28%" height={32} sx={{ mb: 2 }} />
                  <Skeleton variant="rounded" height={400} />
                </CardContent>
              </Card>
            </>
          ) : (
            data && (
              <>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          Attendance Breakdown
                        </Typography>
                        <Box sx={{ height: 250, display: "flex", justifyContent: "center" }}>
                          <Doughnut
                            data={buildAttendanceDoughnutChart({ granularity, data, selectedStudent })}
                            options={{
                              maintainAspectRatio: false,
                              plugins: { legend: { position: "bottom" } },
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {data.student?.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {data.student?.email}
                          {(data.student?.studentId || data.student?.rollNumber || data.student?.rollNo) && (
                            <> • {[data.student?.studentId, data.student?.rollNumber ?? data.student?.rollNo].filter(Boolean).join(" • ")}</>
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {data.student?.class} {data.student?.section ? `(${data.student.section})` : ""} • {data.student?.department} • {data.student?.institute}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Attendance Details
                    </Typography>
                    {data.attendance?.metricsNote ? (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {data.attendance.metricsNote}
                      </Alert>
                    ) : null}
                    <Box sx={{ height: 400 }}>
                      <DataGrid
                        rows={(data.attendance?.details || []).map((d, i) => ({ id: i, ...d }))}
                        columns={attendanceDetailColumns}
                        pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
                        disableRowSelectionOnClick
                        initialState={{ sorting: { sortModel: [{ field: "lectureDate", sort: "desc" }] } }}
                        sx={{ border: "none" }}
                      />
                    </Box>
                  </CardContent>
                </Card>

                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Assignment Details
                    </Typography>
                    <Box sx={{ height: 400 }}>
                      <DataGrid
                        rows={(data.assignments?.details || []).map((d, i) => ({ id: d.id || i, ...d }))}
                        columns={assignmentDetailColumns}
                        pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
                        disableRowSelectionOnClick
                        initialState={{ sorting: { sortModel: [{ field: "dueDate", sort: "desc" }] } }}
                        sx={{ border: "none" }}
                      />
                    </Box>
                  </CardContent>
                </Card>

                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Study Materials
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {data.materials?.total ?? 0} material(s) uploaded for this class in the selected period
                    </Typography>
                    <Box sx={{ height: 400 }}>
                      <DataGrid
                        rows={(data.materials?.details || []).map((d, i) => ({ id: d.id || i, ...d }))}
                        columns={materialDetailColumns}
                        pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
                        disableRowSelectionOnClick
                        initialState={{ sorting: { sortModel: [{ field: "createdAt", sort: "desc" }] } }}
                        sx={{ border: "none" }}
                      />
                    </Box>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Exam Details
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {data.exams?.marked ?? 0} of {data.exams?.total ?? 0} exam(s) marked
                      {data.exams?.total > 0
                        ? ` • Avg ${formatPercent(data.exams?.avgPercent ?? 0)} • Pass rate ${formatPercent(data.exams?.passRate ?? 0)}`
                        : ""}
                    </Typography>
                    <Box sx={{ height: 400 }}>
                      <DataGrid
                        rows={(data.exams?.details || []).map((d, i) => ({ id: d.id || i, ...d }))}
                        columns={examDetailColumns}
                        pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
                        disableRowSelectionOnClick
                        sx={{ border: "none" }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </>
            )
          )}
        </>
      )}
    </Box>
  );
};

export default AdvancedStudentReports;
